import type { Row } from "./api";
import { validateTaxFields } from "./field-validation";
import {
  buildIntakeCards,
  intakeAnswerState,
} from "./components/IntakeExperience";
import type { IntakeCard } from "./components/IntakeExperience";
import { validateIntakeValue } from "./intake-values";

export type TaxTopicId =
  | "personal-information"
  | "filing-status"
  | "dependents"
  | "income"
  | "w2-information"
  | "other-income"
  | "deductions"
  | "credits"
  | "payments-withholding"
  | "review"
  | "federal-return";
export interface TaxTopicDefinition {
  id: TaxTopicId;
  label: string;
  description: string;
}
export interface TopicLocation {
  container: "answers" | "facts";
  id: string;
  path: string[];
}
export interface TopicOverlap {
  id: string;
  kind: "fact-question" | "shared-checklist";
  label: string;
  preferred: TopicLocation;
  duplicate: TopicLocation;
}
export interface TopicProjection {
  kind: "LOCAL_DRAFT_ITEM_SUBSET";
  itemIds: string[];
  preserveHidden: true;
}
export interface TaxTopicBinding extends IntakeCard {
  sourceSchema?: Row;
  projection?: TopicProjection;
}
export interface TaxTopicBlock {
  id: string;
  title: string;
  help?: string;
  bindings: TaxTopicBinding[];
  unavailable?: boolean;
}
export interface TaxTopic extends TaxTopicDefinition {
  blocks: TaxTopicBlock[];
  coveredQuestionIds: string[];
  overlapBindings: TopicOverlap[];
  technicalBindings: TaxTopicBinding[];
}
export interface TopicConflict {
  id: string;
  kind:
    | "DIFFERENT_VALUES"
    | "UNRESOLVED_DUPLICATE"
    | "LEGACY_ANSWER_NOT_COMPARABLE";
  label: string;
  message: string;
  preferred: TopicLocation;
  duplicate: TopicLocation;
}

export const topics: readonly TaxTopicDefinition[] = [
  {
    id: "personal-information",
    label: "Personal Information",
    description:
      "Enter the taxpayer’s name, contact information, residence and personal details.",
  },
  {
    id: "filing-status",
    label: "Filing Status",
    description:
      "Review marital status and the circumstances that affect filing status.",
  },
  {
    id: "dependents",
    label: "Dependents",
    description:
      "Review dependents, qualifying people and whether someone else can claim the taxpayer.",
  },
  {
    id: "income",
    label: "Income",
    description:
      "Review wages, benefits and the income topics that apply to this return.",
  },
  {
    id: "w2-information",
    label: "W-2 Information",
    description:
      "Organize wage statements and identify missing, corrected or duplicate documents.",
  },
  {
    id: "other-income",
    label: "Other Income",
    description:
      "Check income beyond W-2 wages and international or territory circumstances.",
  },
  {
    id: "deductions",
    label: "Deductions",
    description:
      "Review adjustments, additional deductions and itemized expenses.",
  },
  {
    id: "credits",
    label: "Credits",
    description:
      "Review the taxpayer’s circumstances for tax-credit follow-up.",
  },
  {
    id: "payments-withholding",
    label: "Payments & Withholding",
    description:
      "Check payment sources and withholding information without calculating a refund.",
  },
  {
    id: "review",
    label: "Review",
    description:
      "Review filing history, additional circumstances and unresolved information.",
  },
  {
    id: "federal-return",
    label: "Federal Return",
    description:
      "Review return preferences and preparation availability. A completed return is not yet available.",
  },
];

const questionTopics: Record<string, TaxTopicId> = {
  "Q-001": "personal-information",
  "Q-002": "review",
  "Q-003": "review",
  "Q-004": "review",
  "Q-005": "review",
  "Q-006": "personal-information",
  "Q-007": "personal-information",
  "Q-008": "filing-status",
  "Q-009": "dependents",
  "Q-010": "personal-information",
  "Q-011": "w2-information",
  "Q-012": "w2-information",
  "Q-013": "w2-information",
  "Q-015": "payments-withholding",
  "Q-016": "income",
  "Q-017": "other-income",
  "Q-018": "income",
  "Q-019": "deductions",
  "Q-020": "deductions",
  "Q-022": "credits",
  "Q-023": "review",
  "Q-024": "payments-withholding",
  "Q-025": "deductions",
  "Q-026": "review",
  "Q-027": "other-income",
  "Q-028": "review",
};
const technicalQuestions = new Set([
  "Q-002",
  "Q-005",
  "Q-013",
  "Q-015",
  "Q-018",
]);
const technicalFacts = new Set([
  "return.preparation_date",
  "return.source_cutoff_at",
  "workflow.requested_service",
]);
const directQuestionLeaves: Record<string, Record<string, string[]>> = {
  "Q-001": { "return.tax_year": [] },
  "Q-002": {
    "return.preparation_date": ["preparation_date"],
    "return.source_cutoff_at": ["source_cutoff_at"],
  },
  "Q-003": {
    "return.kind": ["return_kind"],
    "return.accounting_period": ["accounting_period"],
  },
  "Q-004": {
    "return.decedent_case": ["decedent_case"],
    "return.bankruptcy_case": ["bankruptcy_case"],
    "return.special_relief_case": ["special_relief_case"],
  },
  "Q-005": { "workflow.requested_service": [] },
  "Q-007": {
    "taxpayer.citizenship": ["citizenship"],
    "taxpayer.residence_all_year": ["residence_all_year"],
    "taxpayer.residence_jurisdiction": ["residence_jurisdiction"],
  },
  "Q-008": { "taxpayer.filing_status": ["filing_status"] },
  "Q-009": {
    "taxpayer.claimable_status": ["claimable_status"],
    "taxpayer.dependent_count": ["dependent_count"],
    "taxpayer.qualifying_person_count": ["qualifying_person_count"],
  },
  "Q-010": {
    "taxpayer.date_of_birth": ["date_of_birth"],
    "taxpayer.blind": ["blind"],
  },
};
const object = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const own = (value: unknown, key: string): boolean =>
  object(value) && Object.prototype.hasOwnProperty.call(value, key);
const clone = <T>(value: T): T =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));
const storage = (card: IntakeCard): "answers" | "facts" =>
  card.captureKind === "questionnaire" ? "answers" : "facts";
function rawValue(
  selected: Row,
  container: "answers" | "facts",
  id: string,
): unknown {
  return own(selected[container], id) ? selected[container][id] : undefined;
}
function unwrap(value: unknown): { value: unknown; unresolved: boolean } {
  if (object(value) && own(value, "state"))
    return value.state === "KNOWN"
      ? { value: value.value, unresolved: !own(value, "value") }
      : { value: undefined, unresolved: true };
  return { value, unresolved: value === undefined };
}
function atPath(
  value: unknown,
  path: string[],
): { found: boolean; value: unknown } {
  let current = value;
  for (const key of path) {
    if (!own(current, key)) return { found: false, value: undefined };
    current = (current as Row)[key];
  }
  return { found: current !== undefined, value: current };
}
function exactEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right))
    return (
      left.length === right.length &&
      left.every((v, index) => exactEqual(v, right[index]))
    );
  if (object(left) && object(right))
    return (
      Object.keys(left).length === Object.keys(right).length &&
      Object.keys(left).every(
        (key) => own(right, key) && exactEqual(left[key], right[key]),
      )
    );
  return false;
}
function masterItems(catalog: Row, id: string): Row[] {
  const master = (catalog.choiceMasters || []).find(
    (entry: Row) => entry.id === id || entry.detail?.master_id === id,
  );
  const items = master?.items || master?.detail?.items;
  return Array.isArray(items) ? items : [];
}
function makeProjection(card: IntakeCard, itemIds: string[]): TaxTopicBinding {
  if (!itemIds.length)
    return {
      ...card,
      available: false,
      schema: {
        "x-intake-unavailable":
          "The itemized expense topics are not available.",
      },
      sourceSchema: card.schema,
    };
  const projected = clone(card.schema);
  const restrict = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(restrict);
      return;
    }
    if (!object(node)) return;
    if (object(node.properties?.items?.properties)) {
      const itemSchema = node.properties.items;
      itemSchema.properties = Object.fromEntries(
        itemIds
          .filter((id) => own(itemSchema.properties, id))
          .map((id) => [id, itemSchema.properties[id]]),
      );
      itemSchema.required = itemIds.filter((id) =>
        own(itemSchema.properties, id),
      );
    }
    Object.values(node).forEach(restrict);
  };
  restrict(projected);
  return {
    ...card,
    title: "Itemized expenses and prior-year amounts",
    help: "Review these expense categories. Credit, other-tax and election questions are collected in their own topics.",
    schema: projected,
    sourceSchema: card.schema,
    projection: {
      kind: "LOCAL_DRAFT_ITEM_SUBSET",
      itemIds: [...itemIds],
      preserveHidden: true,
    },
  };
}
function factTopic(card: IntakeCard): TaxTopicId {
  const id = card.record.id;
  if (
    id === "taxpayer.marital_status_year_end" ||
    id === "taxpayer.filing_status"
  )
    return "filing-status";
  if (/dependent|qualifying_person|claimable/.test(id)) return "dependents";
  if (/^(?:INT\.)?taxpayer\./.test(id) || id === "return.tax_year")
    return "personal-information";
  if (/^w2\.|^INT\.inventory/.test(id)) return "w2-information";
  if (/^INT\.return|^INT\.refund/.test(id)) return "federal-return";
  return "review";
}
function blockFor(
  card: IntakeCard,
  topicId: TaxTopicId,
): { id: string; title: string; help: string } {
  if (/^INT\.taxpayer\./.test(card.record.id))
    return {
      id: `${topicId}:contact`,
      title: "Taxpayer details",
      help: "Enter the taxpayer’s personal and contact information.",
    };
  if (card.captureKind !== "questionnaire")
    return {
      id: `${topicId}:details`,
      title:
        topicId === "filing-status"
          ? "Marital status"
          : topicId === "w2-information"
            ? "Expected wage statements"
            : "Additional details",
      help: "Enter the information provided for this return.",
    };
  if (!card.available)
    return {
      id: `${topicId}:connected-information`,
      title:
        topicId === "w2-information"
          ? "Wage-statement processing"
          : "Identity verification",
      help: "This information requires a connected document or verification workflow. It is not completed by entering draft details.",
    };
  const blockNames: Partial<Record<TaxTopicId, string>> = {
    "personal-information": "About the taxpayer",
    "filing-status": "Filing circumstances",
    dependents: "Household and dependency",
    income: "Wages and benefits",
    "other-income": "Other income sources",
    deductions: "Deductions and expenses",
    credits: "Credit circumstances",
    "payments-withholding": "Payments made",
    review: "Additional filing circumstances",
  };
  return {
    id: `${topicId}:interview`,
    title: blockNames[topicId] || "Return information",
    help: "Complete the relevant fields and checklists. Unknown information stays open for follow-up.",
  };
}

/** All source questions are accounted for once, including producer-dependent and technical-only questions. */
export function buildTaxTopics(catalog: Row): TaxTopic[] {
  const result: TaxTopic[] = topics.map((topic) => ({
    ...topic,
    blocks: [],
    coveredQuestionIds: [],
    overlapBindings: [],
    technicalBindings: [],
  }));
  const byTopic = new Map(result.map((topic) => [topic.id, topic]));
  const questionCards = buildIntakeCards(catalog, "questionnaire").cards;
  const originals = new Map(
    questionCards.map((card) => [card.record.id, card]),
  );
  const uniqueFacts = new Map<string, IntakeCard>();
  for (const card of [
    ...buildIntakeCards(catalog, "facts").cards,
    ...buildIntakeCards(catalog, "supplemental-facts").cards,
  ])
    uniqueFacts.set(card.record.id, card);
  const add = (
    topicId: TaxTopicId,
    card: TaxTopicBinding,
    technical = false,
  ) => {
    const topic = byTopic.get(topicId)!;
    if (technical) {
      topic.technicalBindings.push(card);
      return;
    }
    const definition = blockFor(card, topicId);
    let block = topic.blocks.find((entry) => entry.id === definition.id);
    if (!block) {
      block = { ...definition, bindings: [], unavailable: !card.available };
      topic.blocks.push(block);
    }
    block.bindings.push(card);
    block.unavailable = block.bindings.every((entry) => !entry.available);
  };
  const coveredFacts = new Set<string>();
  for (const card of questionCards) {
    const id = card.record.id,
      topicId = questionTopics[id];
    if (!topicId)
      throw new Error(
        "A new intake question needs an explicit tax-topic mapping.",
      );
    const topic = byTopic.get(topicId)!;
    topic.coveredQuestionIds.push(id);
    let binding: TaxTopicBinding = card;
    if (id === "Q-025")
      binding = makeProjection(
        card,
        masterItems(catalog, "M-ITEMIZED").map((item) => item.item_id),
      );
    add(topicId, binding, technicalQuestions.has(id));
    for (const [factId, path] of Object.entries(
      directQuestionLeaves[id] || {},
    )) {
      const fact = uniqueFacts.get(factId);
      if (
        !fact ||
        !card.available ||
        !(card.record.factIds || []).includes(factId)
      )
        continue;
      coveredFacts.add(factId);
      topic.overlapBindings.push({
        id: `${id}:${factId}`,
        kind: "fact-question",
        label: fact.title,
        preferred: { container: "answers", id, path: [...path] },
        duplicate: { container: "facts", id: factId, path: [] },
      });
    }
  }
  // Contact fields are first within Personal Information; no capture identity changes.
  const sortedFacts = [...uniqueFacts.values()].sort(
    (a, b) =>
      Number(!/^INT\.taxpayer\./.test(a.record.id)) -
      Number(!/^INT\.taxpayer\./.test(b.record.id)),
  );
  for (const card of sortedFacts)
    if (!coveredFacts.has(card.record.id))
      add(factTopic(card), card, technicalFacts.has(card.record.id));
  const personal = byTopic.get("personal-information")!;
  personal.blocks.sort(
    (a, b) =>
      Number(!a.id.endsWith(":contact")) - Number(!b.id.endsWith(":contact")),
  );
  for (const [masterId, questionId] of [
    ["M-CARRY-ELECTION-SPECIAL", "Q-026"],
    ["M-CREDIT", "Q-022"],
    ["M-OTHER-TAX", "Q-023"],
  ]) {
    if (!originals.has("Q-025") || !originals.has(questionId)) continue;
    for (const item of masterItems(catalog, masterId)) {
      const overlap: TopicOverlap = {
        id: `Q-025:${questionId}:${item.item_id}`,
        kind: "shared-checklist",
        label: item.label,
        preferred: {
          container: "answers",
          id: questionId,
          path: ["items", item.item_id],
        },
        duplicate: {
          container: "answers",
          id: "Q-025",
          path: ["items", item.item_id],
        },
      };
      byTopic.get(questionTopics[questionId])!.overlapBindings.push(overlap);
      byTopic.get("deductions")!.overlapBindings.push(overlap);
    }
  }
  // Detached original Q-025 is read-only technical context, never a second normal editor.
  const originalCarryover = originals.get("Q-025");
  if (originalCarryover)
    byTopic.get("deductions")!.technicalBindings.push(originalCarryover);
  if (!byTopic.get("federal-return")!.blocks.length)
    byTopic.get("federal-return")!.blocks.push({
      id: "federal-return:availability",
      title: "Federal return availability",
      help: "Calculation, preparation and certification are not performed by this interview.",
      bindings: [],
      unavailable: true,
    });
  return result;
}

/** Prefer an actual saved answer. Hydrate compatible exact raw leaves without filling absent fields or saving on read. */
export function topicDisplayValue(
  card: TaxTopicBinding,
  selected: Row,
): unknown {
  const current = rawValue(selected, storage(card), card.record.id);
  if (current !== undefined) {
    if (!card.projection) return clone(current);
    const data = unwrap(current);
    if (data.unresolved || !object(data.value) || !object(data.value.items))
      return clone(current);
    const visible = {
      ...clone(data.value),
      items: Object.fromEntries(
        card.projection.itemIds
          .filter((id) => own((data.value as Row).items, id))
          .map((id) => [id, clone((data.value as Row).items[id])]),
      ),
    };
    return object(current) && own(current, "state")
      ? { ...clone(current), value: visible }
      : visible;
  }
  if (
    card.captureKind !== "questionnaire" ||
    !card.available ||
    card.projection
  )
    return undefined;
  const mappings = directQuestionLeaves[card.record.id];
  const knownSchema = card.schema["x-intake-stateful"]?.valueSchema;
  if (!mappings || !knownSchema) return undefined;
  let assembled: unknown = knownSchema.type === "object" ? {} : undefined;
  let supplied = 0;
  for (const [id, path] of Object.entries(mappings)) {
    const fact = rawValue(selected, "facts", id);
    if (fact === undefined) continue;
    const leafSchema =
      path.length === 0
        ? knownSchema
        : path.length === 1
          ? knownSchema.properties?.[path[0]]
          : undefined;
    if (!leafSchema || !validateIntakeValue(fact, leafSchema).valid) continue;
    if (!path.length) {
      assembled = clone(fact);
      supplied++;
    } else if (object(assembled) && path.length === 1) {
      assembled[path[0]] = clone(fact);
      supplied++;
    }
  }
  return supplied > 0 &&
    validateIntakeValue(assembled, partialQuestionSchema(knownSchema)).valid
    ? assembled
    : undefined;
}

/** Merge only explicitly edited projection leaves. Shared hidden values are retained, never mirrored to another question. */
function partialQuestionSchema(schema: Row): Row {
  const partial = clone(schema);
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!object(node)) return;
    if (Array.isArray(node.required) && !own(node.properties, "state"))
      delete node.required;
    Object.values(node).forEach(visit);
  };
  visit(partial);
  return partial;
}
export function topicSaveValue(
  card: TaxTopicBinding,
  value: unknown,
  selected: Row,
): unknown {
  if (!card.projection) return clone(value);
  const visibleValidation = validateIntakeValue(
    value,
    partialQuestionSchema(card.schema),
  );
  if (!visibleValidation.valid) {
    const issues = validateTaxFields(card.schema, value, {
      sectionId: "deductions",
      sectionLabel: "Deductions",
      rootLabel: card.title,
      rootId: "expenses",
    });
    const issue = issues.find((item) => item.kind === "invalid") || issues[0];
    throw new Error(
      issue
        ? `Deductions — ${issue.fieldLabel}: ${issue.reason}`
        : `Deductions — ${card.title}: The saved expense entry has an unsupported format. Ask an administrator to review this entry.`,
    );
  }
  const next = unwrap(value);
  if (next.unresolved || !object(next.value) || !object(next.value.items))
    throw new Error(
      "Enter the visible expense answers before saving; shared prior answers cannot be replaced by an unknown overall status.",
    );
  const previous = rawValue(selected, "answers", card.record.id);
  if (
    object(previous) &&
    own(previous, "state") &&
    Object.keys(previous).some((key) => key !== "state" && key !== "value")
  )
    throw new Error(
      "The earlier answer contains additional saved information. Technical review is required before changing it.",
    );
  const old = unwrap(previous);
  if (
    previous !== undefined &&
    !old.unresolved &&
    (!object(old.value) ||
      (own(old.value, "items") && !object(old.value.items)))
  )
    throw new Error(
      "The earlier answer cannot be safely combined. Review it before replacing any saved information.",
    );
  if (
    previous !== undefined &&
    old.unresolved &&
    object(previous) &&
    own(previous, "value") &&
    previous.value !== null &&
    previous.value !== undefined
  )
    throw new Error(
      "The earlier unresolved answer contains saved details. Review it before replacing them.",
    );
  const oldValue = object(old.value) ? old.value : {};
  const merged = {
    ...clone(oldValue),
    ...clone(next.value),
    items: {
      ...(object(oldValue.items) ? clone(oldValue.items) : {}),
      ...clone(next.value.items),
    },
  };
  const result =
    object(value) && own(value, "state")
      ? { ...clone(value), value: merged }
      : merged;
  if (
    !card.sourceSchema ||
    !validateIntakeValue(result, partialQuestionSchema(card.sourceSchema)).valid
  )
    throw new Error(
      "An earlier saved detail needs technical review before this change can be saved. No hidden information has been replaced.",
    );
  return result;
}

function readLocation(
  selected: Row,
  location: TopicLocation,
): { stored: boolean; unresolved: boolean; found: boolean; value: unknown } {
  const raw = rawValue(selected, location.container, location.id);
  if (raw === undefined)
    return { stored: false, unresolved: false, found: false, value: undefined };
  const base =
    location.container === "answers"
      ? unwrap(raw)
      : { value: raw, unresolved: false };
  const leaf = atPath(base.value, location.path);
  return { stored: true, unresolved: base.unresolved, ...leaf };
}
/** Values for human-readable conflict comparison. Internal location IDs need not be displayed. */
export function topicConflictValues(
  conflict: TopicConflict,
  selected: Row,
): {
  preferred: {
    stored: boolean;
    unresolved: boolean;
    found: boolean;
    value: unknown;
  };
  duplicate: {
    stored: boolean;
    unresolved: boolean;
    found: boolean;
    value: unknown;
  };
} {
  return {
    preferred: clone(readLocation(selected, conflict.preferred)),
    duplicate: clone(readLocation(selected, conflict.duplicate)),
  };
}
export function topicConflicts(
  topic: TaxTopic,
  selected: Row,
): TopicConflict[] {
  const conflicts: TopicConflict[] = [];
  for (const overlap of topic.overlapBindings) {
    const primary = readLocation(selected, overlap.preferred),
      duplicate = readLocation(selected, overlap.duplicate);
    if (!primary.stored || !duplicate.stored) continue;
    let kind: TopicConflict["kind"] | undefined;
    if (primary.unresolved || duplicate.unresolved)
      kind = "UNRESOLVED_DUPLICATE";
    else if (
      primary.found &&
      duplicate.found &&
      !exactEqual(primary.value, duplicate.value)
    )
      kind = "DIFFERENT_VALUES";
    else if (
      overlap.kind === "fact-question" &&
      (!primary.found || !duplicate.found)
    )
      kind = "LEGACY_ANSWER_NOT_COMPARABLE";
    if (kind)
      conflicts.push({
        id: overlap.id,
        kind,
        label: overlap.label,
        message: `Saved entries for ${overlap.label.toLowerCase()} need to be reconciled. Neither entry has been overwritten.`,
        preferred: clone(overlap.preferred),
        duplicate: clone(overlap.duplicate),
      });
  }
  return conflicts;
}
export function inspectTopicState(
  topic: TaxTopic,
  selected: Row,
): {
  savedCount: number;
  missingCount: number;
  attentionCount: number;
  unavailableCount: number;
  conflicts: TopicConflict[];
  draftReady: boolean;
} {
  const cards = topic.blocks.flatMap((block) => block.bindings);
  let savedCount = 0,
    missingCount = 0,
    attentionCount = 0,
    unavailableCount = 0;
  for (const card of cards) {
    if (!card.available) {
      unavailableCount++;
      continue;
    }
    const actual = rawValue(selected, storage(card), card.record.id);
    const value = topicDisplayValue(card, selected);
    if (actual !== undefined) savedCount++;
    if (value === undefined) missingCount++;
    else if (
      intakeAnswerState(value) === "needs-attention" ||
      !validateIntakeValue(value, card.schema).valid
    )
      attentionCount++;
  }
  const conflicts = topicConflicts(topic, selected);
  return {
    savedCount,
    missingCount,
    attentionCount,
    unavailableCount,
    conflicts,
    draftReady:
      cards.length > 0 &&
      !missingCount &&
      !attentionCount &&
      !unavailableCount &&
      !conflicts.length,
  };
}

/** Presentation only. An explicit no may hide a detail panel; it never deletes saved answers or establishes eligibility. */
export function detailVisibility(
  value: unknown,
  schema?: Row,
): {
  visible: boolean;
  reason:
    | "UNANSWERED"
    | "UNKNOWN_OR_CONFLICT"
    | "INCOMPLETE"
    | "EXPLICIT_NO"
    | "EXPLICIT_NOT_APPLICABLE"
    | "DETAILS_REQUIRED";
  preserveSavedData: true;
} {
  if (value === undefined)
    return { visible: true, reason: "UNANSWERED", preserveSavedData: true };
  if (schema && !validateIntakeValue(value, schema).valid)
    return { visible: true, reason: "INCOMPLETE", preserveSavedData: true };
  if (object(value) && value.state === "NOT_APPLICABLE")
    return {
      visible: false,
      reason: "EXPLICIT_NOT_APPLICABLE",
      preserveSavedData: true,
    };
  const data = unwrap(value);
  if (data.unresolved || intakeAnswerState(value) === "needs-attention")
    return {
      visible: true,
      reason: "UNKNOWN_OR_CONFLICT",
      preserveSavedData: true,
    };
  if (data.value === false || data.value === "NO" || data.value === "ABSENT")
    return { visible: false, reason: "EXPLICIT_NO", preserveSavedData: true };
  if (data.value === "NOT_APPLICABLE")
    return {
      visible: false,
      reason: "EXPLICIT_NOT_APPLICABLE",
      preserveSavedData: true,
    };
  return {
    visible: true,
    reason:
      object(data.value) && !Object.keys(data.value).length
        ? "INCOMPLETE"
        : "DETAILS_REQUIRED",
    preserveSavedData: true,
  };
}
